import _ from "lodash";
import React, {
  memo,
  useState,
  useEffect,
  useRef,
  useImperativeHandle,
} from "react";

import classes from "./CurriculumOptions.scss";
import classNames from "classnames";
import I18nHOC from "UIComponents/I18nHOC";
import { FormTextInput } from "UIComponents/InputTextField";
import { FormSelectDropdown } from "UIComponents/SelectDropdown";
import InputNumberFieldV2 from "UIComponents/InputNumberFieldV2";
import FullScreenLoader from "UIComponents/FullScreenLoader";
import propTypes from "prop-types";
import { graphql, compose } from "lib/apolloClient";
import { getOrganizationGlobalConstantsQuery } from "modules/CommonQuery";
import { getCurriculumProgramDataFromProgramId } from "./modules/CurriculumOptionModule";
import { getGlobalGradesQuery } from "./modules/CurriculumOptionsQuery";
import { getGlobalGradesFromCache } from "./modules/CurriculumOptionGraphQLHelpers.js";
import { connect } from "react-redux";
import {
  CURRICULUM_TYPE_DP,
  CURRICULUM_TYPE_UBD,
} from "Constants/stringConstants";
import {
  CURRICULUM_PLANS,
  SUBSCRIPTION_PLANS,
  SUBSCRIPTION_STATUS,
  CURRICULUM_GROUPS,
  LANGUAGE_OPTIONS,
  CURRICULUM_PILOT_PLANS,
  PRO_PLAN_IDS,
} from "Constants";
import ExamSessionComp from "Tooling/routes/OrganizationV2/components/ExamSessionComp";
import TimezoneComp from "Tooling/routes/OrganizationV2/components/TimezoneComp";
import {
  getCurriculumProgramAcronym,
  getCurriculumProgramName,
  MAXIMUM_ACRONYM_LENGTH,
  getGlobalGradesForCurriculumProgram,
} from "./modules/CurriculumOptionsUtils";
import {
  UBD_CURRICULUM_VARIANTS,
  MULTI_PROGRAM_DATA_LANGUAGE_ENABLED_CURRICULUMS,
} from "Tooling/modules/ToolingUtils";
import { useMount } from "Hooks";
import ACLStore from "lib/aclStore";
import { ALLOW_MULTIPLE_PROGRAM_LANGUAGES_IN_NON_DP_PROGRAMS } from "Constants/permissionConstants";
import {
  ReloadArrowOutlined,
  WarningTriangleFilled,
} from "@toddle-edu/ds-icons";
import { IconButton, TextInput } from "@toddle-edu/ds-web";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";

const CurriculumOptions = ({
  mode,
  t,
  curriculumProgram,
  curriculumProgramOptions,
  updateCurriculumProgram,
  toddleBuddiesOptions,
  globalGradeOptions,
  customRef,
  examSessionTimezoneFromQuery,
  setToastMsg,
}) => {
  const {
    type,
    variant,
    name,
    toddleBuddy,
    plan,
    grades,
    examSessionMonth,
    acronym,
    isNewCurriculumProgram,
    orgDpExamSession,
    programDataLanguages,
    examSessionTimezone,
    dealId,
    platformId = "",
    studentCount,
    curriculumProgramData,
  } = curriculumProgram;

  const [curriculumProgramValue, setCurriculumProgramValue] = useState(
    curriculumProgramData
  );
  const [platformIdReceived, setPlatformIdReceived] = useState(false);
  const [receivedPlan, setReceivedPlan] = useState(null);
  const [synced, setSynced] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [inputFieldErrors, setInputFieldErrors] = useState({});

  const shouldDisablePlatformId =
    mode === "editCurriculumPrograms" && !_.isEmpty(platformIdReceived);

  const nameRef = useRef();

  const acronymRef = useRef();

  const typeRef = useRef();

  const toddleBuddyRef = useRef();

  const gradeRef = useRef();

  const planRef = useRef();

  const previousTypeRef = useRef(type);

  const previousVariantRef = useRef(variant);

  const platformIdRef = useRef();

  useImperativeHandle(customRef, () => {
    return {
      isValid: () => {
        const refs = [
          typeRef,
          nameRef,
          acronymRef,
          toddleBuddyRef,
          planRef,
          gradeRef,
          platformIdRef,
        ];

        const totalErrors = _.reduce(
          refs,
          (result, ref) => {
            if (ref === platformIdRef) {
              if (customValidationForPlatformId()) {
                return result;
              }
              return result + 1;
            }
            return result + ref.current.isValid();
          },
          0
        );

        return totalErrors;
      },
    };
  });

  useEffect(() => {
    if (
      (!_.isEmpty(type) && previousTypeRef.current !== type) ||
      previousVariantRef.current !== variant
    ) {
      const defaultName = getCurriculumProgramName({
        type,
        variant,
      });

      const defaultAcronym = getCurriculumProgramAcronym({
        type,
        variant,
      });
      updateCurriculumProgram({
        id: curriculumProgram.id,
        name: defaultName,
        acronym: defaultAcronym,
        variant,
        dealId,
        platformId,
      });

      nameRef.current.resetErrorState();
      acronymRef.current.resetErrorState();
    }
  }, [type, variant]);

  useEffect(() => {
    previousTypeRef.current = type;
    previousVariantRef.current = variant;
  }, [type, variant]);

  useEffect(() => {
    setCurriculumProgramValue(curriculumProgramData);
  }, [curriculumProgramData]);

  useMount(() => {
    if (platformId) setPlatformIdReceived(platformId);
    if (plan) setReceivedPlan(plan);
  });

  const resetPlatformIdErrorState = () => {
    setInputFieldErrors({
      platformId: "",
    });
  };

  const updateCurriculumProgramType = value => {
    let payload;

    if (_.includes(UBD_CURRICULUM_VARIANTS, value)) {
      payload = {
        id: curriculumProgram.id,
        type: CURRICULUM_TYPE_UBD,
        variant: value,
        grades: [],
      };
    } else {
      payload = {
        id: curriculumProgram.id,
        type: value,
        variant: null,
        grades: [],
      };
    }
    updateCurriculumProgram(payload);
  };
  const curriculumPlansOptions = CURRICULUM_PLANS;
  const curriculumPilotPlansIds = _.map(
    CURRICULUM_PILOT_PLANS,
    ({ value }) => value
  );
  const subscriptionPlanOptions = SUBSCRIPTION_PLANS;
  const subscriptionStatusOptions = SUBSCRIPTION_STATUS;
  const curriculumGroupOptions = CURRICULUM_GROUPS;

  const validateAcronym = value => {
    const length = _.size(value);
    if (length > MAXIMUM_ACRONYM_LENGTH) {
      return t("onBoarding:programme_acronym_warning");
    }
    return "";
  };

  const validatePlan = value => {
    if (
      studentCount > 0 &&
      value !== receivedPlan &&
      _.includes(PRO_PLAN_IDS, value)
    ) {
      return t("onBoarding:plan_switching_error_text");
    }
    return "";
  };

  const isProgramIdRequired = () => {
    return !_.isEmpty(plan) && !_.includes(curriculumPilotPlansIds, plan);
  };

  const customValidationForPlatformId = () => {
    if (isProgramIdRequired() && _.isEmpty(platformId)) {
      setInputFieldErrors({
        platformId: t("onBoarding:programId_is_required_for_non_pilot_plans"),
      });
      return 0;
    }
    if (!_.isEmpty(platformId) && !synced) {
      setInputFieldErrors({
        platformId: t("onBoarding:sync_required"),
      });
      return 0;
    }

    resetPlatformIdErrorState();
    return 1;
  };

  const updateExamSessionTimezone = param => {
    updateCurriculumProgram({ id: curriculumProgram.id, ...param });
  };
  const updateCurriculumGroup = (key, value) => {
    setCurriculumProgramValue(prev => ({ ...prev, [key]: value }));
    updateCurriculumProgram({
      id: curriculumProgram.id,
      curriculumProgramData: {
        ...curriculumProgramData,
        [key]: value,
      },
    });
  };

  const updateMultipleTagsValue = updates => {
    setCurriculumProgramValue(prev => ({ ...prev, ...updates }));
    updateCurriculumProgram({
      id: curriculumProgram.id,
      curriculumProgramData: {
        ...curriculumProgramData,
        ...updates,
      },
    });
  };

  const resetCurriculumState = () => {
    updateMultipleTagsValue({
      subscriptionPlan: null,
      subscriptionStatus: null,
      crmId: null,
    });
  };

  const updatePlatformId = params => {
    const value = params.programId;
    if (!value) {
      resetCurriculumState();
    }
    if (!_.isNull(_.get(inputFieldErrors, "platformId", null))) {
      resetPlatformIdErrorState();
    }
    if (synced) {
      setSynced(false);
    }
    updateCurriculumProgram({
      id: curriculumProgram.id,
      platformId: value,
    });
  };

  const normalizeOptionLabel = option =>
    React.isValidElement(option.label) && option.normalisedLabel
      ? { ...option, name: option.normalisedLabel }
      : option;

  const [nonIbOptions, ibOptions] = _.partition(
    _.map(curriculumProgramOptions, normalizeOptionLabel),
    option => option.type === CURRICULUM_TYPE_UBD
  );

  const handlePlatformSync = async () => {
    if (!platformId) return;

    try {
      setIsLoading(true);
      const curriculumData = await getCurriculumProgramDataFromProgramId({
        id: platformId,
      });
      const error = _.get(curriculumData, "error.message", null);
      if (!isNewCurriculumProgram && platformIdReceived === platformId) {
        setToastMsg({
          msg: "Sync successful",
          locale_params: [
            {
              isPlainText: true,
            },
          ],
          type: "success",
        });
      } else {
        if (error) {
          setToastMsg({
            msg: error,
            locale_params: [
              {
                isPlainText: true,
              },
            ],
            type: "error",
          });
          resetCurriculumState();
          return;
        }
      }

      const updates = [
        "subscriptionPlan",
        "subscriptionStatus",
        "crmId",
      ].reduce((acc, key) => {
        const value = _.get(curriculumData, key, null);
        if (value) acc[key] = value;
        return acc;
      }, {});

      if (Object.keys(updates).length > 0) {
        updateMultipleTagsValue(updates);
      }

      setSynced(true);
    } catch (error) {
      setToastMsg("toastMsgs:something_went_wrong");
    } finally {
      setIsLoading(false);
    }
  };

  const isMultiProgramDataLanguageEnabled = _.includes(
    MULTI_PROGRAM_DATA_LANGUAGE_ENABLED_CURRICULUMS,
    type
  );

  return (
    <div className={classes.container}>
      <div className={classes.partContainer}>
        <FormSelectDropdown
          options={[
            {
              label: "IB Programs",
              value: "IB_PROGRAMS",
              options: ibOptions,
            },
            {
              label: "Non IB Programs",
              value: "NON_IB_PROGRAMS",
              options: nonIbOptions,
            },
          ]}
          value={variant ? variant : type}
          size="x-large"
          updateInputField={updateCurriculumProgramType}
          placeholder={t("onBoarding:choose_curriculum_program")}
          isRequired
          label={t("onBoarding:select_curriculum")}
          ref={typeRef}
          disabled={!isNewCurriculumProgram}
        />
        <div className={classes.flexRow}>
          <div className={classes.input}>
            <FormTextInput
              value={name}
              updateInputField={value =>
                updateCurriculumProgram({
                  id: curriculumProgram.id,
                  name: value,
                })
              }
              size="x-large"
              isRequired
              label={t("onBoarding:enter_programme_name")}
              placeholder={t("common:enter_text_here")}
              ref={nameRef}
            />
          </div>
          <div className={classes.input}>
            <FormTextInput
              value={acronym}
              updateInputField={value =>
                updateCurriculumProgram({
                  id: curriculumProgram.id,
                  acronym: value,
                })
              }
              size="x-large"
              label={t("onBoarding:enter_programme_acronym")}
              placeholder={t("common:enter_text_here")}
              isRequired
              ref={acronymRef}
              customValidation={validateAcronym}
            />
          </div>
        </div>
        <div className={classes.flexRow}>
          <div className={classes.input}>
            <FormSelectDropdown
              value={toddleBuddy}
              size="x-large"
              updateInputField={value =>
                updateCurriculumProgram({
                  id: curriculumProgram.id,
                  toddleBuddy: value,
                })
              }
              options={toddleBuddiesOptions}
              placeholder={t("onBoarding:select_toddle_buddy")}
              isRequired
              label={t("onBoarding:select_toddle_buddy")}
              ref={toddleBuddyRef}
              menuPlacement="top"
            />
          </div>
          <div className={classes.input}>
            <FormSelectDropdown
              value={plan}
              options={curriculumPlansOptions}
              updateInputField={value => {
                updateCurriculumProgram({
                  id: curriculumProgram.id,
                  plan: value,
                });
                resetPlatformIdErrorState();
              }}
              size="x-large"
              placeholder={t("onBoarding:select_plan")}
              label={t("onBoarding:select_plan")}
              isRequired
              customValidation={validatePlan}
              ref={planRef}
              menuPlacement="top"
            />
          </div>
        </div>

        <FormSelectDropdown
          value={grades}
          multi
          size="x-large"
          options={globalGradeOptions}
          placeholder={t("onBoarding:select_grades_applicable")}
          updateInputField={values =>
            updateCurriculumProgram({
              id: curriculumProgram.id,
              grades: values,
            })
          }
          isRequired
          label={t("onBoarding:select_grades_for_programme")}
          ref={gradeRef}
          menuPlacement="top"
        />

        {programDataLanguages ? (
          <FormSelectDropdown
            placeholder={t("onBoarding:choose_language")}
            options={LANGUAGE_OPTIONS}
            size={"x-large"}
            value={programDataLanguages}
            label={t("onBoarding:program_data_language")}
            updateInputField={params => {
              updateCurriculumProgram({
                id: curriculumProgram.id,
                programDataLanguages: _.isArray(params) ? params : [params],
              });
            }}
            menuPlacement="top"
            multi={
              ACLStore.can(
                ALLOW_MULTIPLE_PROGRAM_LANGUAGES_IN_NON_DP_PROGRAMS
              ) || isMultiProgramDataLanguageEnabled
            }
          />
        ) : null}

        {type == CURRICULUM_TYPE_DP ? (
          <ExamSessionComp
            examSessionMonth={examSessionMonth}
            disabled={
              _.isEqual(mode, "editCurriculumPrograms")
                ? !_.isEmpty(orgDpExamSession)
                : null
            }
            updateOrgObject={param =>
              updateCurriculumProgram({ id: curriculumProgram.id, ...param })
            }
          />
        ) : null}

        {type === CURRICULUM_TYPE_DP ? (
          <TimezoneComp
            examSessionMonth={examSessionMonth}
            examSessionTimezone={examSessionTimezone}
            examSessionTimezoneFromQuery={examSessionTimezoneFromQuery}
            updateOrgObject={updateExamSessionTimezone}
          />
        ) : null}
      </div>
      <div className={classes.tagContainer}>
        <div className={classes.tagInput}>
          <FormSelectDropdown
            value={_.get(curriculumProgramValue, "subscriptionPlan")}
            size="x-large"
            options={subscriptionPlanOptions}
            placeholder={t("onBoarding:select_subscription_plan")}
            label={t("onBoarding:select_subscription_plan")}
            menuPlacement="top"
            disabled={true}
          />
        </div>
        <div className={classes.tagInput}>
          <FormSelectDropdown
            value={_.get(curriculumProgramValue, "subscriptionStatus")}
            size="x-large"
            disabled={true}
            options={subscriptionStatusOptions}
            placeholder={t("onBoarding:select_subscription_status")}
            label={t("onBoarding:select_subscription_status")}
            menuPlacement="top"
          />
        </div>
        <div className={classes.tagInput}>
          <FormSelectDropdown
            value={_.get(curriculumProgramValue, "curriculumGroup")}
            size="x-large"
            updateInputField={value =>
              updateCurriculumGroup("curriculumGroup", value)
            }
            options={curriculumGroupOptions}
            placeholder={t("onBoarding:select_curriculum_group")}
            label={t("onBoarding:select_curriculum_group")}
            menuPlacement="top"
          />
        </div>
        <InputNumberFieldV2
          value={dealId}
          size="x-large"
          updateInputField={value => {
            updateCurriculumProgram({
              id: curriculumProgram.id,
              dealId: value,
            });
          }}
          label={t("onBoarding:enter_deal_id")}
          placeholder={t("common:enter_number_here")}
        />
        <div className={classes.platformIdInput}>
          <div style={{ width: "78%" }}>
            <TextInput
              value={platformId}
              name="programId"
              onChange={e => updatePlatformId({ programId: e.target.value })}
              label={t("onBoarding:enter_program_id")}
              isRequired={isProgramIdRequired()}
              ref={platformIdRef}
              placeholder={t("common:enter_text_here")}
              error={_.get(inputFieldErrors, "platformId", "")}
              customValidation={customValidationForPlatformId}
              size="large"
              disabled={shouldDisablePlatformId}
            />
            {_.get(inputFieldErrors, "platformId", "") ? (
              <div className={classNames(classes.errorText, "text-body-3")}>
                <WarningTriangleFilled size="xxxx-small" variant="critical" />
                {_.get(inputFieldErrors, "platformId", "")}
              </div>
            ) : null}
          </div>
          <IconButton
            icon={<ReloadArrowOutlined size="xx-small" variant="subtle" />}
            variant="outlined-subtle"
            size="large"
            onClick={handlePlatformSync}
          />
        </div>
      </div>
      {isLoading ? <FullScreenLoader /> : null}
    </div>
  );
};

const mapStateToProps = () => {
  return {
    isLoading: false,
    isData: true,
    examSessionTimezoneFilter: {
      names: ["EXAM_SESSION_TIMEZONE"],
    },
  };
};

const mapActionCreators = {
  setToastMsg,
};

export default compose(
  I18nHOC({ resource: ["onBoarding"] }),
  connect(mapStateToProps, mapActionCreators),
  graphql(getGlobalGradesQuery, {
    name: "getGlobalGrades",
    options: () => ({
      fetchPolicy: "cache-and-network",
    }),
    props: ({
      getGlobalGrades,
      ownProps: {
        isData,
        isLoading,
        curriculumProgram,
        initiallySelectedGlobalGrades,
      },
    }) => {
      const globalGrades = getGlobalGradesFromCache();
      const curriculumType = _.get(curriculumProgram, "type", null);
      const globalGradeOptions = _.map(
        getGlobalGradesForCurriculumProgram({
          curriculumProgramType: curriculumType,
          globalGrades,
          initiallySelectedGlobalGrades,
        }),
        ({ id, name }) => ({
          value: id,
          label: name,
        })
      );
      return {
        globalGrades,
        globalGradeOptions,
        isData: !_.isEmpty(getGlobalGrades) && isData,
        isLoading:
          _.includes([1, 2], getGlobalGrades["networkStatus"]) || isLoading,
      };
    },
  }),
  graphql(getOrganizationGlobalConstantsQuery, {
    name: "getExamSessionTimezone",
    skip: ({ curriculumProgram, organizationId }) =>
      _.get(curriculumProgram, "type") !== CURRICULUM_TYPE_DP ||
      _.isEmpty(organizationId),
    options: ({ organizationId, examSessionTimezoneFilter }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        organizationId,
        filter: examSessionTimezoneFilter,
      },
    }),
    props: ({ getExamSessionTimezone, ownProps: { isData, isLoading } }) => {
      const examSessionTimezoneFromQuery = _.get(
        getExamSessionTimezone,
        "node.globalConstants.0.value",
        {}
      );

      return {
        examSessionTimezoneFromQuery,
        isData: !_.isEmpty(getExamSessionTimezone) && isData,
        isLoading:
          _.includes([1, 2], getExamSessionTimezone["networkStatus"]) ||
          isLoading,
      };
    },
  })
)(memo(CurriculumOptions));

CurriculumOptions.propTypes = {
  mode: propTypes.string,
  t: propTypes.func,
  curriculumProgram: propTypes.object,
  curriculumProgramOptions: propTypes.array,
  updateCurriculumProgram: propTypes.func,
  toddleBuddiesOptions: propTypes.array,
  globalGradeOptions: propTypes.array,
  curriculumProgramIndex: propTypes.number,
  updateCurriculumProgramInputRefs: propTypes.func,
};
